import { useCallback, useState } from 'react';
import { useBusiness } from './useBusiness';
import { EXTRA_PHRASES, SERVICE_PHRASES, autoTranslate } from './profile/serviceExample';
import { MORE_PHRASES } from './profile/serviceDictionary';
import type { ServiceDto, ServiceFieldsInput } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import { formatAmount } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { Tag } from '../../ui/controls';
import { Alert, EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';
import { Field, FieldPair, FormError, Note, Row, TextInput } from './settings/parts';

/**
 * «Услуги и цены» кабинета (ТЗ §14.1).
 *
 * Список показывает ВСЕ услуги, включая выключенные. Спрятав выключенные, экран заставил бы
 * владельца гадать, куда делась услуга: он её не удалял, а в списке её нет — это выглядит
 * как потеря данных, а не как его собственное действие.
 *
 * УДАЛЕНИЯ НЕТ ВОВСЕ, И ЭТО РЕШЕНИЕ, А НЕ ПРОБЕЛ. У записей внешний ключ `onDelete: Restrict`:
 * «удалить» работало бы у новой услуги и падало бы у той, по которой уже приходили, — то есть
 * кнопка вела бы себя по-разному в зависимости от прошлого, которого владелец не помнит.
 * Плюс история визитов клиента не должна разваливаться от кадровых решений. Вместо удаления —
 * тумблер «Активна», и об этом сказано текстом (`itemNoDelete`) там, где владелец ищет
 * кнопку «Удалить», — в форме правки.
 */
/** Значение пункта «Другая услуга» в списке: не совпадает ни с одним названием. */
const CUSTOM = '\u0000custom';

export function ServicesScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const { businessId } = props;
  // Пример услуги под отрасль: у репетитора «Урок математики», а не «Стрижка».
  const type = useBusiness(businessId).business?.type;
  const phrases = type ? SERVICE_PHRASES[type] : [];
  // Для автоперевода «Другой услуги» — список плюс запасной словарь, которого нет в выпадашке.
  const dictionary = type ? [...phrases, ...EXTRA_PHRASES[type], ...MORE_PHRASES[type]] : [];

  const list = useAsync(
    useCallback(() => api.cabinet.listServices(businessId), [api, businessId]),
    [businessId],
  );

  const [form, setForm] = useState<ServiceForm | null>(null);
  // Своё название: выбрано «Другая услуга» или услуга заведена с именем не из списка.
  // Тогда два поля — русское и узбекское — владелец заполняет сам.
  const [customPicked, setCustomPicked] = useState(false);
  const custom =
    customPicked || (form !== null && form.titleRu !== '' && !phrases.some((p) => p[0] === form.titleRu));
  const [formProblem, setFormProblem] = useState<FormProblem | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  /** Отказ переключателя активности. Своя шторка: список под ней остаётся виден. */
  const [toggleError, setToggleError] = useState<unknown>(null);
  /**
   * Выключена ПОСЛЕДНЯЯ активная услуга у опубликованной организации: карточка осталась
   * в каталоге, а записаться в неё не на что. Флаг приходит от бэкенда — он же считает
   * активные услуги и специалистов; экран его не выводит, чтобы два места не разошлись.
   */
  const [emptyShelf, setEmptyShelf] = useState(false);

  const closeForm = (): void => {
    setForm(null);
    setCustomPicked(false);
    setFormProblem(null);
    setSaveError(null);
  };

  const save = async (): Promise<void> => {
    if (!form || busy) return;
    const fields = readServiceForm(form);
    if (typeof fields === 'string') {
      setFormProblem(fields);
      return;
    }

    setBusy(true);
    setFormProblem(null);
    setSaveError(null);
    try {
      if (form.id) await api.cabinet.updateService(businessId, form.id, fields);
      else await api.cabinet.createService(businessId, fields);
      closeForm();
      list.reload();
    } catch (error) {
      setSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (service: ServiceDto): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setToggleError(null);
    try {
      const result = await api.cabinet.setServiceActive(businessId, service.id, !service.active);
      setEmptyShelf(result.unbookableInCatalog);
      list.reload();
    } catch (error) {
      setToggleError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen>
      <TopBar title={t('srvTitle')} onBack={props.onBack} />

      <ScrollArea bottomGap="cta">
        {list.state.status === 'loading' ? (
          <Loader />
        ) : list.state.status === 'failed' ? (
          <ErrorState error={list.state.error} onRetry={list.reload} />
        ) : (
          <Section>
            <SectionTitle>{t('srvTitle')}</SectionTitle>
            {list.state.data.length === 0 ? (
              <EmptyState>{t('srvEmpty')}</EmptyState>
            ) : (
              list.state.data.map((service) => (
                <Row key={service.id} dimmed={!service.active}>
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setFormProblem(null);
                        setSaveError(null);
                        setForm(formOf(service));
                      }}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="truncate text-[14.5px] font-semibold">
                        {lang === 'ru' ? service.titleRu : service.titleUz}
                      </div>
                      <div className="mt-0.5 text-[12px] text-ink-3">
                        {service.durationMin} {t('min')} · {formatAmount(service.priceSum, lang)}{' '}
                        {t('sum')}
                      </div>
                    </button>
                    <Tag tone={service.active ? 'ok' : 'cancelled'}>
                      {t(service.active ? 'itemActive' : 'itemInactive')}
                    </Tag>
                  </div>
                  <div className="mt-2.5 flex">
                    <SmallButton
                      variant={service.active ? 'ghost' : 'accent'}
                      disabled={busy}
                      onClick={() => void toggleActive(service)}
                    >
                      {t(service.active ? 'itemDisable' : 'itemEnable')}
                    </SmallButton>
                  </div>
                </Row>
              ))
            )}
            <Note>{t('itemNoDelete')}</Note>
          </Section>
        )}
      </ScrollArea>

      <CtaBar>
        <CtaButton
          onClick={() => {
            setFormProblem(null);
            setSaveError(null);
            setForm(emptyForm());
          }}
          disabled={list.state.status !== 'ready'}
        >
          {t('srvAdd')}
        </CtaButton>
      </CtaBar>

      <BottomSheet
        open={form !== null}
        title={t(form?.id ? 'srvEdit' : 'srvAdd')}
        onClose={closeForm}
      >
        {form ? (
          <>
            {/* Название — выбором из типовых услуг отрасли: оба языка берутся из одной пары
                словаря. Нет нужной — «Другая услуга» открывает два поля для своего названия. */}
            <Field label={t('srvName')}>
              <select
                value={custom ? CUSTOM : form.titleRu}
                onChange={(event) => {
                  if (event.target.value === CUSTOM) {
                    setCustomPicked(true);
                    setForm({ ...form, titleRu: '', titleUz: '' });
                    return;
                  }
                  const pair = phrases.find((p) => p[0] === event.target.value);
                  if (pair) {
                    setCustomPicked(false);
                    setForm({ ...form, titleRu: pair[0], titleUz: pair[1] });
                  }
                }}
                className="w-full appearance-none rounded-[12px] bg-surface-2 px-3.5 py-3 text-[14.5px] font-semibold text-ink outline-none focus:shadow-[inset_0_0_0_1.5px_var(--accent)]"
              >
                <option value="" disabled>
                  {t('srvPick')}
                </option>
                {phrases.map((p) => (
                  <option key={p[0]} value={p[0]}>
                    {lang === 'ru' ? p[0] : p[1]}
                  </option>
                ))}
                <option value={CUSTOM}>{t('srvCustom')}</option>
              </select>
            </Field>
            {custom ? (
              <>
                <Field label={t('srvNameRu')}>
                  <TextInput
                    value={form.titleRu}
                    onChange={(value) =>
                      setForm({ ...form, titleRu: value, titleUz: autoTranslate(dictionary, value, 0, form.titleUz) })
                    }
                    placeholder={t('srvNamePh')}
                  />
                </Field>
                <Field label={t('srvNameUz')}>
                  <TextInput
                    value={form.titleUz}
                    onChange={(value) =>
                      setForm({ ...form, titleUz: value, titleRu: autoTranslate(dictionary, value, 1, form.titleRu) })
                    }
                    placeholder={t('srvNamePhUz')}
                  />
                </Field>
              </>
            ) : null}
            <FieldPair>
              <Field label={t('srvDuration')}>
                <TextInput
                  value={form.durationMin}
                  onChange={(value) => setForm({ ...form, durationMin: value })}
                  placeholder="30"
                  numeric
                />
              </Field>
              <Field label={t('srvPrice')}>
                <TextInput
                  value={form.priceSum}
                  onChange={(value) => setForm({ ...form, priceSum: value })}
                  placeholder="80000"
                  numeric
                />
              </Field>
            </FieldPair>

            {formProblem ? <FormError text={t(formProblem)} /> : null}
            {saveError ? <ErrorState error={saveError} withTitle={false} /> : null}

            {/* Владелец ищет здесь кнопку «Удалить» — прототип её показывал. Вместо неё
                объяснение, почему её нет и что делает тумблер активности. */}
            {form.id ? <Note>{t('itemNoDelete')}</Note> : null}

            <CtaButton onClick={() => void save()} disabled={busy}>
              {t('itemSave')}
            </CtaButton>
            <CtaButton variant="ghost" onClick={closeForm} className="mt-2">
              {t('itemCancel')}
            </CtaButton>
          </>
        ) : null}
      </BottomSheet>

      {/* Организация в каталоге осталась без витрины. Шторка, а не строка в списке:
          её нельзя пролистать мимо, а публикацию бэкенд намеренно не снимает сам —
          снять легко, вернуть некому, — так что исправлять владельцу. */}
      <BottomSheet
        open={emptyShelf}
        title={t('bizEmptyShelfT')}
        onClose={() => setEmptyShelf(false)}
      >
        <Alert tone="danger">{t('bizEmptyShelf')}</Alert>
        <CtaButton variant="ghost" onClick={() => setEmptyShelf(false)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      <BottomSheet
        open={toggleError !== null}
        title={t('errTitle')}
        onClose={() => setToggleError(null)}
      >
        <ErrorState error={toggleError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setToggleError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

/** Поля формы — строки, а не числа: пустое поле числом не выражается, а `NaN` — не ввод. */
interface ServiceForm {
  readonly id: string | null;
  readonly titleRu: string;
  readonly titleUz: string;
  readonly durationMin: string;
  readonly priceSum: string;
}

type FormProblem = 'srvErrName' | 'srvErrDuration' | 'srvErrPrice';

function emptyForm(): ServiceForm {
  return { id: null, titleRu: '', titleUz: '', durationMin: '', priceSum: '' };
}

function formOf(service: ServiceDto): ServiceForm {
  return {
    id: service.id,
    titleRu: service.titleRu,
    titleUz: service.titleUz,
    durationMin: String(service.durationMin),
    priceSum: String(service.priceSum),
  };
}

/**
 * Разбор формы. Ключ проблемы вместо услуги — то же самое отношение к вводу, что у бэкенда
 * к запросу: не «подставим 30 минут по умолчанию», а «скажем, что не так».
 *
 * Оба названия обязательны, потому что клиент видит своё: пустое узбекское название
 * означало бы пустую строку в каталоге у половины пользователей.
 */
function readServiceForm(form: ServiceForm): ServiceFieldsInput | FormProblem {
  const titleRu = form.titleRu.trim();
  const titleUz = form.titleUz.trim();
  if (!titleRu || !titleUz) return 'srvErrName';

  const durationMin = readInteger(form.durationMin);
  if (durationMin === null || durationMin <= 0) return 'srvErrDuration';

  const priceSum = readInteger(form.priceSum);
  if (priceSum === null || priceSum < 0) return 'srvErrPrice';

  return { titleRu, titleUz, durationMin, priceSum };
}

/**
 * Целое из поля. Пробелы внутри числа убираются: «80 000» — обычный способ записать цену,
 * и отказ на нём был бы придиркой, а не защитой. Всё остальное — `null`, то есть видимая
 * ошибка ввода: `Number('12abc')` даёт `NaN`, и подставить вместо него ноль значило бы
 * выставить услугу бесплатной.
 */
function readInteger(text: string): number | null {
  const cleaned = text.replace(/\s/g, '');
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isSafeInteger(value) ? value : null;
}
